import type { CrmRequiredActionItem } from '../crm/requiredActions';

/** Same floor used by GBP/social draft generation and auto-reply. */
export const MARKETING_AI_TOKENS_MIN = 150;

export type MarketingAiTokensPausedReason =
  | 'limit_reached'
  | 'payment_failed'
  | 'payment_failed_ack'
  | null
  | string;

export interface MarketingAiTokensSnapshot {
  /** Spendable AI tokens: plan allowance remaining plus purchased balance. */
  tokenBalance: number;
  pausedReason: MarketingAiTokensPausedReason;
  updatedAt?: string | null;
}

export interface MarketingAutomationFlags {
  gbpAutoPost: boolean;
  gbpAutoReply: boolean;
  gbpReviewInstagramShare: boolean;
  socialAutoPost: boolean;
}

export function hasActiveMarketingAutomation(flags: MarketingAutomationFlags): boolean {
  return (
    flags.gbpAutoPost ||
    flags.gbpAutoReply ||
    flags.gbpReviewInstagramShare ||
    flags.socialAutoPost
  );
}

export function marketingAiTokensNeedAttention(tokens: MarketingAiTokensSnapshot): boolean {
  const paused =
    tokens.pausedReason === 'payment_failed' || tokens.pausedReason === 'payment_failed_ack';
  return tokens.tokenBalance < MARKETING_AI_TOKENS_MIN || paused;
}

/**
 * One Control Centre action when marketing automations are on but tokens are
 * too low or auto-topup payment has failed. Ephemeral — no DB write.
 */
export function buildMarketingAiTokensRequiredActions(
  tokens: MarketingAiTokensSnapshot | null,
  flags: MarketingAutomationFlags,
  now: Date = new Date(),
): CrmRequiredActionItem[] {
  if (!tokens || !hasActiveMarketingAutomation(flags) || !marketingAiTokensNeedAttention(tokens)) {
    return [];
  }

  const paymentFailed =
    tokens.pausedReason === 'payment_failed' || tokens.pausedReason === 'payment_failed_ack';
  // Always treat as current urgency so it is not buried under older leads.
  const nowMs = now.getTime();

  return [
    {
      kind: 'marketing_ai_tokens_blocked',
      title: paymentFailed ? 'Auto-topup failed' : 'AI usage running out',
      customerName: 'Marketing automations',
      subtitle: paymentFailed
        ? 'Auto-topup failed — marketing automations may stop'
        : 'Plan AI usage and tokens are almost gone — auto-posts/replies will stop',
      sortOrder: nowMs,
      createdAt: tokens.updatedAt || now.toISOString(),
    },
  ];
}
